// ==========================
// Version 2 — pages/AdminDashboard.js
// - Adds Open Flags count
// - Recent Admin Activity (latest 8)
// - 30-day Memories trend (inline SVG bar chart)
// - 7-day Sparkline (inline SVG)
// - Still works with your isAdmin() Firestore rules
// ==========================
import React from 'react';
import {
  collection,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';

export default function AdminDashboard() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const [totals, setTotals] = React.useState({
    users: null,
    memories: null,
    openFlags: null,
  });

  const [activity, setActivity] = React.useState([]); // [{id, action, actorUid, targetUid, createdAt}]
  const [memDaily, setMemDaily] = React.useState([]); // 30-day buckets: [{label, count, date}]
  const [mem7, setMem7] = React.useState([]);         // last 7 daily counts (for sparkline)

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        // --- Counts (users, memories, open flags) ---
        const usersP = getCountFromServer(collection(db, 'users'));
        const memsP = getCountFromServer(collection(db, 'memories'));
        const openFlagsP = getCountFromServer(
          query(collection(db, 'flags'), where('status', '==', 'open'))
        );

        // --- Recent admin activity (latest 8) ---
        const actP = getDocs(
          query(collection(db, 'admin_activity'), orderBy('createdAt', 'desc'), limit(8))
        );

        // --- Memories trend (pull recent docs; aggregate client-side)
        // Pull up to ~500 most-recent memories; that’s usually plenty for 30 days even on busy apps.
        const recentMemsSnapP = getDocs(
          query(collection(db, 'memories'), orderBy('createdAt', 'desc'), limit(500))
        );

        const [usersSnap, memsSnap, flagsSnap, actSnap, recentMemsSnap] = await Promise.all([
          usersP, memsP, openFlagsP, actP, recentMemsSnapP
        ]);

        if (!alive) return;

        setTotals({
          users: usersSnap.data().count ?? 0,
          memories: memsSnap.data().count ?? 0,
          openFlags: flagsSnap.data().count ?? 0,
        });

        const acts = actSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setActivity(acts);

        // Aggregate memories by day for last 30 days
        const now = new Date();
        const dayKey = (dt) => {
          const y = dt.getFullYear();
          const m = String(dt.getMonth() + 1).padStart(2, '0');
          const d = String(dt.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        };
        const labelOf = (dt) => dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

        // Build a map of last 30 days initialized to 0
        const days = [];
        const dayMap = new Map();
        for (let i = 29; i >= 0; i--) {
          const dt = new Date(now);
          dt.setDate(now.getDate() - i);
          dt.setHours(0,0,0,0);
          const key = dayKey(dt);
          dayMap.set(key, { date: new Date(dt), label: labelOf(dt), count: 0 });
          days.push(key);
        }

        recentMemsSnap.docs.forEach(doc => {
          const createdAt = doc.data()?.createdAt;
          // protect against null createdAt while uploads are pending
          if (createdAt?.toDate) {
            const dt = createdAt.toDate();
            const key = dayKey(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()));
            if (dayMap.has(key)) {
              dayMap.get(key).count += 1;
            }
          }
        });

        const ordered = days.map(k => dayMap.get(k));
        setMemDaily(ordered);

        // last 7 for sparkline
        setMem7(ordered.slice(-7));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[AdminDashboard] Failed to load analytics:', e);
        setError('Could not load admin analytics.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div style={page}>
      <h1 style={h1}>Admin</h1>
      <p style={sub}>Quick overview of platform activity.</p>

      {error ? <div style={err}>{error}</div> : null}

      {/* Top stat cards */}
      <div style={grid}>
        <StatCard title="Total Users" value={totals.users} loading={loading} />
        <StatCard title="Total Memories" value={totals.memories} loading={loading} />
        <StatCard title="Open Flags" value={totals.openFlags} loading={loading} pill />
      </div>

      {/* Charts row */}
      <div style={{ ...grid, marginTop: 12 }}>
        <ChartCard
          title="Memories — last 30 days"
          loading={loading}
          content={
            <Bars30 data={memDaily} />
          }
          footer={memDaily?.length ? (
            <div style={chartLegend}>
              <span style={legendDot} /> Daily count • {sum(memDaily)} total
            </div>
          ) : null}
        />
        <ChartCard
          title="Last 7 days (sparkline)"
          loading={loading}
          content={<Spark7 data={mem7} />}
          footer={mem7?.length ? (
            <div style={chartLegend}>
              <span style={legendDot} /> Last 7 total: {sum(mem7)}
            </div>
          ) : null}
        />
      </div>

      {/* Recent admin activity */}
      <div style={{ marginTop: 18 }}>
        <div style={sectionTitle}>Recent Admin Activity</div>
        <div style={activityList}>
          {loading ? (
            <div style={muted}>Loading…</div>
          ) : activity.length === 0 ? (
            <div style={muted}>No recent admin actions.</div>
          ) : (
            activity.map(a => (
              <div key={a.id} style={activityRow}>
                <div style={{ fontWeight: 800 }}>{prettyAction(a.action)}</div>
                <div style={{ color: WARM_GRAY }}>
                  {a.actorUid ? `by ${a.actorUid}` : '—'}
                  {a.targetUid ? ` • target ${a.targetUid}` : ''}
                </div>
                <div style={{ marginLeft: 'auto', color: WARM_GRAY }}>
                  {a?.createdAt?.toDate ? a.createdAt.toDate().toLocaleString() : '—'}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- stat & layout components ---------- */

function StatCard({ title, value, loading, pill = false }) {
  return (
    <div style={card}>
      <div style={muted}>{title}</div>
      <div style={bigNum}>
        {loading ? <span style={dash}>—</span> : value}
      </div>
      {pill ? (
        <div style={{ marginTop: 8 }}>
          <span style={pillStyle}>{loading ? '—' : (value === 0 ? 'All clear' : 'Needs attention')}</span>
        </div>
      ) : null}
    </div>
  );
}

function ChartCard({ title, content, footer, loading }) {
  return (
    <div style={card}>
      <div style={{ ...muted, marginBottom: 8 }}>{title}</div>
      <div style={{ height: 160, display: 'grid', alignItems: 'center' }}>
        {loading ? <div style={skeleton} /> : content}
      </div>
      {footer ? <div style={{ marginTop: 8 }}>{footer}</div> : null}
    </div>
  );
}

/* ---------- inline SVG charts (no external libs) ---------- */

// 30-day bars
function Bars30({ data }) {
  if (!Array.isArray(data) || data.length === 0) return <div style={muted}>No data</div>;
  const max = Math.max(...data.map(d => d.count), 1);
  const width = 480;   // responsive feel; container is fluid anyway
  const height = 120;
  const gap = 2;
  const barW = (width - (data.length - 1) * gap) / data.length;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Last 30 days memories">
      {data.map((d, i) => {
        const h = Math.round((d.count / max) * (height - 18)); // reserve top space
        const x = i * (barW + gap);
        const y = height - h;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              fill={GOLD}
              opacity="0.85"
              rx="3"
            />
          </g>
        );
      })}
    </svg>
  );
}

// 7-day sparkline
function Spark7({ data }) {
  if (!Array.isArray(data) || data.length === 0) return <div style={muted}>No data</div>;
  const width = 480;
  const height = 120;
  const max = Math.max(...data.map(d => d.count), 1);
  const step = width / (data.length - 1 || 1);

  const points = data.map((d, i) => {
    const x = i * step;
    const y = height - Math.round((d.count / max) * (height - 12));
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Last 7 days sparkline">
      <polyline
        points={points}
        fill="none"
        stroke={GOLD}
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity="0.9"
      />
      {/* Circle on last point */}
      {data.length > 0 ? (() => {
        const i = data.length - 1;
        const x = i * step;
        const y = height - Math.round((data[i].count / max) * (height - 12));
        return <circle cx={x} cy={y} r="4" fill={NAVY} stroke={GOLD} strokeWidth="2" />;
      })() : null}
    </svg>
  );
}

/* ---------- helpers ---------- */

function prettyAction(a) {
  switch (String(a || '')) {
    case 'resolve_flag': return 'Resolved Flag';
    case 'reopen_flag': return 'Reopened Flag';
    case 'hide_memory': return 'Hid Memory';
    case 'unhide_memory': return 'Unhid Memory';
    case 'delete_memory': return 'Deleted Memory';
    case 'role_change': return 'Role Change';
    default: return a || '—';
  }
}
function sum(arr) {
  return (arr || []).reduce((s, x) => s + (x?.count || 0), 0);
}

/* --- styles --- */
const page = { minHeight: 'calc(100dvh - 64px)', background: NAVY, color: IVORY, padding: 16 };
const h1 = { fontFamily: 'serif', margin: '6px 0 6px' };
const sub = { color: WARM_GRAY, margin: '0 0 12px' };
const err = { color: BURGUNDY, background: 'rgba(92,26,43,0.08)', border: '1px dashed rgba(183,110,121,0.55)', padding: 10, borderRadius: 10, margin: '8px 0' };

const grid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 12,
  maxWidth: 1100,
  margin: '0 auto',
};

const card = {
  background: IVORY,
  color: NAVY,
  borderRadius: 16,
  border: '1px solid rgba(201,164,76,0.35)',
  boxShadow: '0 8px 28px rgba(10,26,51,0.25)',
  padding: 16,
};

const bigNum = { fontFamily: 'serif', fontSize: 28, fontWeight: 800 };
const muted = { color: WARM_GRAY, fontSize: 12 };
const dash = { display: 'inline-block', width: 22, textAlign: 'center' };
const pillStyle = {
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 800,
  padding: '2px 8px',
  borderRadius: 999,
  color: NAVY,
  background: GOLD,
  border: `1px solid ${GOLD}`,
};

const skeleton = {
  height: 120,
  borderRadius: 10,
  background: 'linear-gradient(90deg, rgba(10,26,51,0.06) 25%, rgba(10,26,51,0.13) 37%, rgba(10,26,51,0.06) 63%)',
  backgroundSize: '400% 100%',
  animation: 'bwak-shimmer 1.2s ease-in-out infinite',
  border: '1px solid rgba(10,26,51,0.18)',
};

const sectionTitle = { fontFamily: 'serif', fontSize: 18, margin: '8px 0' };
const activityList = {
  background: IVORY,
  color: NAVY,
  borderRadius: 16,
  border: '1px solid rgba(201,164,76,0.35)',
  boxShadow: '0 8px 28px rgba(10,26,51,0.25)',
  padding: 8,
};
const activityRow = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  borderBottom: '1px solid rgba(10,26,51,0.08)',
  padding: '10px 6px',
};
const chartLegend = { display: 'flex', alignItems: 'center', gap: 8, color: WARM_GRAY, fontSize: 12 };
const legendDot = { width: 10, height: 10, borderRadius: 6, display: 'inline-block', background: GOLD, border: `1px solid ${GOLD}` };

// keyframes (injected once)
(() => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('bwak-shimmer-style')) return;
  const el = document.createElement('style');
  el.id = 'bwak-shimmer-style';
  el.textContent = `
    @keyframes bwak-shimmer {
      0% { background-position: 0% 0; }
      100% { background-position: 100% 0; }
    }
  `;
  document.head.appendChild(el);
})();

// ==========================
// End Version 2 — pages/AdminDashboard.js
// ==========================
